import { type ThunkDispatch } from "@reduxjs/toolkit";

import React, { useEffect } from "react";
import { connect, useSelector } from "react-redux";

import { TermsModal } from "igfd-ui/src/app-index";

import { useAuthState } from "@dashboard/context/AuthStateContext";
import {
  acceptTermsAndConditions,
  fetchTermsAcceptance,
} from "@dashboard/store/actions/termsAcceptanceActions";
import { type TermsAcceptanceActions } from "@dashboard/store/reducers/termsAcceptanceReducer";
import { type ReduxState } from "@dashboard/types";
import { type ThunkExtraArgument } from "@dashboard/types/thunk";

const RestaurantTermsAcceptanceComponent = ({
  fetchTermsAcceptance,
  acceptTermsAndConditions,
}: {
  fetchTermsAcceptance: (restaurantUuid: string) => void;
  acceptTermsAndConditions: (restaurantUuid: string) => void;
}) => {
  const { logout } = useAuthState();
  const userIsAdmin = useSelector(
    (state: ReduxState) => state.dashboardApp.userIsAdmin
  );

  const { termsAcceptanceStatusLoading, termsAcceptanceStatus } = useSelector(
    (state: ReduxState) => state.termsAcceptance
  );

  const selectedRestaurant = useSelector(
    (state: ReduxState) => state.restaurant.selectedRestaurant!
  );

  useEffect(() => {
    if (termsAcceptanceStatus === null) {
      fetchTermsAcceptance(selectedRestaurant.uuid);
    }
  }, [termsAcceptanceStatus, fetchTermsAcceptance, selectedRestaurant.uuid]);

  if (termsAcceptanceStatus === null || termsAcceptanceStatusLoading) {
    return null;
  }

  if (userIsAdmin) {
    // Admin users don't need to accept terms.
    return null;
  }

  return (
    <TermsModal
      type="restaurant"
      open={termsAcceptanceStatus === false}
      onAccept={() => {
        acceptTermsAndConditions(selectedRestaurant.uuid);
      }}
      onReject={logout}
    />
  );
};

export const RestaurantTermsAcceptanceModal = connect(
  null,
  (
    dispatch: ThunkDispatch<
      ReduxState,
      ThunkExtraArgument,
      TermsAcceptanceActions
    >
  ) => ({
    fetchTermsAcceptance: (restaurantUuid: string) =>
      dispatch(fetchTermsAcceptance(restaurantUuid)),
    acceptTermsAndConditions: (restaurantUuid: string) =>
      dispatch(acceptTermsAndConditions(restaurantUuid)),
  })
)(RestaurantTermsAcceptanceComponent);
